import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { genres as fallbackGenres } from '../data/mockBooks';

const cache = { all: null, withBooks: null };
const inflight = { all: null, withBooks: null };

function fetchGenres(withBooks) {
  const key = withBooks ? 'withBooks' : 'all';
  if (cache[key]) return Promise.resolve(cache[key]);
  if (!inflight[key]) {
    inflight[key] = api.get(`/genres${withBooks ? '?withBooks=true' : ''}`)
      .then((data) => {
        cache[key] = (data.genres || []).map((g) => g.name);
        return cache[key];
      })
      .catch(() => fallbackGenres)
      .finally(() => { inflight[key] = null; });
  }
  return inflight[key];
}

// Admin-managed genre list, used everywhere the storefront previously read
// the static `genres` array from data/mockBooks.js. Falls back to that same
// array (used as the seed default server-side too) until the fetch resolves
// or if it fails, so nothing renders empty.
//
// Pass { withBooks: true } to only get genres that currently have at least
// one live book — used by browse/filter surfaces (home genre highlights,
// shop filter) so an empty genre never shows up as a dead end. Genre pickers
// used while authoring a book (Publish/EditBook) should keep the full list.
export function useGenres({ withBooks = false } = {}) {
  const [genres, setGenres] = useState(cache[withBooks ? 'withBooks' : 'all'] || (withBooks ? [] : fallbackGenres));

  useEffect(() => {
    let cancelled = false;
    fetchGenres(withBooks).then((g) => { if (!cancelled) setGenres(g); });
    return () => { cancelled = true; };
  }, [withBooks]);

  return genres;
}
